import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@jeraisonne/db';
import type { ChallengeMediaType } from '@jeraisonne/db';
import { ChallengeMediaError, challengeVideoFormat } from './challenge-media-format';

type DB = SupabaseClient<Database>;

/**
 * Délai maximal de la capture de la vignette. Sur iPhone, une vidéo hors écran peut ne JAMAIS charger ses images
 * (ni `loadeddata` ni `seeked`) : sans délai la publication restait bloquée pour toujours, la vidéo envoyée mais
 * jamais reliée au défi. Passé ce délai, la vignette est la vidéo elle-même.
 */
export const THUMBNAIL_TIMEOUT_MS = 6_000;

export function captureVideoThumbnail(
  videoBlob: Blob,
  timeoutMs: number = THUMBNAIL_TIMEOUT_MS,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(videoBlob);
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.onloadeddata = video.onseeked = video.onerror = null;
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      fn();
    };
    const timer = setTimeout(
      () => finish(() => reject(new Error('Video thumbnail timeout'))),
      timeoutMs,
    );
    video.muted = true;
    video.playsInline = true;
    // « metadata » ne charge aucune image sur iOS : il faut les données
    video.preload = 'auto';

    video.onloadeddata = () => {
      video.currentTime = 0.01;
    };

    video.onseeked = () => {
      if (!video.videoWidth || !video.videoHeight) {
        finish(() => reject(new Error('Video has no frame')));
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        finish(() => reject(new Error('Canvas context unavailable')));
        return;
      }
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(
        (blob) => finish(() => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')))),
        'image/jpeg',
        0.85,
      );
    };

    video.onerror = () => finish(() => reject(new Error('Video load error')));

    video.src = url;
    video.load();
  });
}

export async function uploadChallengeMedia(
  db: DB,
  userId: string,
  slug: string,
  file: Blob,
  mediaType: Exclude<ChallengeMediaType, 'none'>,
  filenameSuffix = '',
  videoMimeOverride?: { contentType: string; ext: string },
): Promise<string> {
  let ext = 'jpg';
  let contentType = 'image/jpeg';
  if (mediaType === 'video') {
    // le type du fichier lui-même d'abord (une vidéo de l'appareil photo de
    // l'iPhone est un .mov « video/quicktime ») ; celui de l'enregistreur sinon
    const name = 'name' in file && typeof file.name === 'string' ? file.name : '';
    const fmt =
      challengeVideoFormat(file.type, name) ??
      (videoMimeOverride ? challengeVideoFormat(videoMimeOverride.contentType) : null);
    if (!fmt)
      throw new ChallengeMediaError(
        'Format vidéo non pris en charge : enregistre la vidéo ici avec le bouton rouge.',
      );
    ext = fmt.ext;
    contentType = fmt.contentType;
  }
  const path = `${userId}/${slug}${filenameSuffix}.${ext}`;
  // Le type envoyé est celui de la partie du formulaire : on ré-étiquette le
  // fichier (un .mov est un mp4 pour le navigateur qui le lira).
  const body = file.type === contentType ? file : new Blob([file], { type: contentType });

  const { error } = await db.storage.from('challenges').upload(path, body, {
    contentType,
    upsert: false,
  });
  if (error) throw error;

  const { data } = db.storage.from('challenges').getPublicUrl(path);
  return data.publicUrl;
}

export async function updateChallengeMedia(
  db: DB,
  slug: string,
  mediaUrl: string,
  thumbnailUrl: string,
): Promise<void> {
  const { error } = await db
    .from('challenge_posts')
    .update({ media_url: mediaUrl, thumbnail_url: thumbnailUrl })
    .eq('challenge_slug', slug);
  if (error) throw error;
}
